import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { localDate, type Row } from "./types";
import { dayLabel, estimateSize, topVisible, viewableMedia } from "./rows";
import { findMatches, monthsOf, type Month } from "./search";
import { Bubble } from "./Bubble";
import { SearchSheet } from "./SearchSheet";
import { ThemeButton } from "./ThemeButton";
import { Viewer } from "./Viewer";

/** The scrolling river: every conversation, oldest first, one row per
 *  renderable thing -- plus the two ways out of it, search and the viewer.
 *
 *  Two things about the day header are worth knowing before you change it.
 *
 *  1. The header a row carries is INLINE, inside the row, so the height the
 *     virtualizer reserved is the height the row actually takes. A separate
 *     header row would be a second item per day to keep in step with the index.
 *  2. The chip pinned to the top is NOT sticky and is not in the scroller at
 *     all -- it is an overlay above it. `position: sticky` inside a virtual item
 *     silently does nothing (items are absolutely positioned, so a sticky
 *     child's containing block is the ROW, and it sticks within its own 40 px).
 *     Sticky as a direct child of the scroller would work, but it would occupy
 *     flow at the top and shift every item down by its own height, putting the
 *     virtualizer's offsets and the real ones permanently out of step.
 *
 *  The chip needs no scroll listener and no state. The virtualizer already
 *  re-renders on scroll, so reading the topmost visible item during render is
 *  free -- and it must be the topmost VISIBLE one, because getVirtualItems()
 *  begins `overscan` rows above the fold. The search sheet and the viewer are
 *  overlays for the same reason: anything that takes up flow at the top of the
 *  scroller moves every row underneath it.
 */
export function Timeline({ rows }: { rows: Row[] }) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [vp, setVp] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));

  const [searching, setSearching] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [query, setQuery] = useState("");
  /** Which match the arrows are standing on. -1 while you are still looking. */
  const [active, setActive] = useState(-1);
  const [viewerAt, setViewerAt] = useState<number | null>(null);
  const opener = useRef<HTMLElement | null>(null);

  // A rotation changes every reserved height at once. Without re-measuring, the
  // list keeps portrait heights in landscape and stays wrong until you have
  // scrolled past every row.
  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  const virt = useVirtualizer<HTMLDivElement, HTMLDivElement>({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (i) => estimateSize(rows, i, vp.w, vp.h),
    overscan: 12,
    initialRect: { width: vp.w, height: vp.h },
    // A measurement of 0 means the element is not laid out -- not that the row
    // is empty. Collapsing the slot to nothing on that basis loses the row and
    // takes the scroll position with it, so keep the estimate instead.
    measureElement: (el, entry, instance) => {
      const box = entry?.borderBoxSize?.[0];
      const measured = box ? Math.round(box.blockSize) : el.offsetHeight;
      return measured || instance.options.estimateSize(instance.indexFromElement(el));
    },
  });

  useEffect(() => { virt.measure(); }, [virt, vp.w, vp.h]);

  // 0.5 ms over 19,000 rows, measured -- so this runs on every keystroke and
  // there is nothing to debounce.
  const matches = useMemo(() => findMatches(rows, query), [rows, query]);
  const months = useMemo(() => monthsOf(rows), [rows]);
  const viewable = useMemo(() => viewableMedia(rows), [rows]);
  const ranges = useMemo(
    () => new Map(matches.map((m) => [m.index, m.ranges])),
    [matches],
  );
  const activeRow = active >= 0 && active < matches.length ? matches[active].index : -1;

  // Centred rather than pinned to the top: the point of jumping to a message is
  // to read what was said around it.
  //
  // And no retry loop. virtual-core re-targets a scrollToIndex on its own every
  // frame as rows measure, and gives up after five seconds -- a second scroller
  // layered on top of that fights it and lands somewhere neither meant.
  useEffect(() => {
    if (activeRow >= 0) virt.scrollToIndex(activeRow, { align: "center" });
  }, [virt, activeRow]);

  const openMedia = useCallback((rowIndex: number, el: HTMLElement) => {
    const at = viewable.indexOf(rowIndex);
    if (at < 0) return;
    opener.current = el;
    setViewerAt(at);
  }, [viewable]);

  const closeViewer = useCallback(() => {
    setViewerAt(null);
    // Otherwise focus falls to the top of the document and the next keypress
    // scrolls three years back to the beginning.
    opener.current?.focus();
  }, []);

  const changeQuery = (q: string) => {
    setQuery(q);
    setActive(-1);
    setExpanded(true);
  };

  const step = (delta: number) => {
    if (matches.length === 0) return;
    // Wraps, unlike the viewer's arrows: you are counting matches here, and
    // dead-ending on the last one just means pressing the other arrow twelve
    // times to get back.
    setActive((a) => (a + delta + matches.length) % matches.length);
  };

  const jumpToMonth = (m: Month) => {
    // 'start', so the month's own day rule lands at the top of the screen.
    virt.scrollToIndex(m.index, { align: "start" });
    setSearching(false);
    setQuery("");
    setActive(-1);
    setExpanded(true);
  };

  const closeSearch = () => {
    setSearching(false);
    setQuery("");
    setActive(-1);
    setExpanded(true);
  };

  const items = virt.getVirtualItems();
  const top = topVisible(items, virt.scrollOffset ?? 0);

  if (!rows.length) {
    // A blank screen after a successful login reads as a broken app, not as an
    // empty database. `make ingest` is the answer, so say so.
    return <p className="empty">Nothing in the archive yet — run <code>make ingest</code>.</p>;
  }

  return (
    <div className="timeline">
      {searching ? (
        <SearchSheet
          rows={rows}
          query={query}
          onQuery={changeQuery}
          matches={matches}
          months={months}
          active={active}
          expanded={expanded}
          onExpand={() => setExpanded(true)}
          onPick={(i) => { setActive(i); setExpanded(false); }}
          onStep={step}
          onMonth={jumpToMonth}
          onClose={closeSearch}
        />
      ) : (
        // One absolutely-positioned row holding two static children, rather
        // than two absolute positions kept in step by hand -- the theme button
        // used to be pinned 52px down, which hardcoded the search button's own
        // height. Both leave together: the sheet is an overlay over exactly
        // this corner, and a button underneath it is unreachable, not merely
        // ugly.
        <div className="topleft">
          <button type="button" className="searchbtn" aria-label="search messages"
                  onClick={() => setSearching(true)}>&#128269;</button>
          <ThemeButton />
        </div>
      )}

      {top && <div className="dayheader">{dayLabel(localDate(rows[top.index]))}</div>}

      <div ref={parentRef} className="scroller">
        <div style={{ height: virt.getTotalSize(), position: "relative" }}>
          {items.map((v) => {
            const hit = ranges.get(v.index);
            return (
              <div
                key={v.key}
                ref={virt.measureElement}
                data-index={v.index}
                style={{ position: "absolute", top: 0, left: 0, width: "100%",
                         transform: `translateY(${v.start}px)` }}
              >
                <Bubble
                  rows={rows}
                  index={v.index}
                  vw={vp.w}
                  vh={vp.h}
                  highlight={hit && { ranges: hit, active: v.index === activeRow }}
                  onOpenMedia={openMedia}
                />
              </div>
            );
          })}
        </div>
      </div>

      {viewerAt !== null && (
        <Viewer
          rows={rows}
          order={viewable}
          at={viewerAt}
          onAt={setViewerAt}
          onClose={closeViewer}
          vw={vp.w}
          vh={vp.h}
        />
      )}
    </div>
  );
}
